import { Component, ElementRef, Injector, ViewChild } from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { MessageService } from 'primeng/api';
import { Observable } from 'rxjs/internal/Observable';
import { environment } from '../../../../environments/environment';
import { Art57Section } from '../../ui/Art57Section/Art57Section';
import { Util } from '../../ui/Util';

@Component({
  selector: 'formly-field-file',
  styleUrls: ['./file-selector-component.css'],
  template: ` 
             <p *ngIf="isCreateAndSendProductSection()">
             <span class="d-inline-block"  data-bs-toggle="tooltip" title="Open dialog to choose document file">
             Click <a class="attachmentLink" (click)="onSelectAttachment(field,$event);">here</a> to select the file 
<!--             <i class="pi pi-folder icon" aria-hidden="true"></i>-->
             </span>

             <span  *ngIf="field.parent?.parent?.model.file && (field.parent?.parent?.model.file.length > 0)"
             class="d-inline-block"  data-bs-toggle="tooltip" title="View selected document"  (click)="openFileInNewWindow(field)">
              <i class="pi pi-eye icon"></i>
             </span>
              <input type="file" [accept]="acceptTypes" #fileInput [formControl]="formControl" (change)="onChange(field,$event)" class="fileclass" /> 
            </p> 
            `,
})
export class FileSelectorComponent extends FieldType<FieldTypeConfig> {
      @ViewChild('fileInput') fileInput!: ElementRef;
      acceptTypes: string = '';
  private selectionId = 0;

      constructor( private messageService: MessageService,
                   private injector: Injector
      ) {
        super();
      }

      ngOnInit() {
        this.field.parent!.get!('filetype').formControl!.valueChanges.subscribe(() => {
          this.updateAcceptTypes();
        });
        this.updateAcceptTypes();
      }

      openFileInNewWindow(field: any) {
        let file: File | undefined;
        if (field.parent.parent.model.file && field.parent.parent.model.file.length > 0) {
          file = field.parent.parent.model.file[0];
        }
        
        if (file) {
          const fileURL = URL.createObjectURL(file);

          const a = document.createElement('a');
          a.href = fileURL;
          a.download = file.name;
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(fileURL),100); 
        }    
      }
        getFileNameAndExtension(path: string): { fileName: string, extension: string } {
          // Extract the filename with extension
          const fullName = path.substring(path.lastIndexOf('/') + 1);
          const lastDotIndex = fullName.lastIndexOf('.');

          if (lastDotIndex === -1) {
              return { fileName: fullName, extension: '' };
          }

          const fileName = fullName.substring(0, lastDotIndex);
          const extension = fullName.substring(lastDotIndex + 1);

          return { fileName, extension };
      }

      getFieldTypeValue(type: string): number|null {

        switch (type.toUpperCase()) {
            case "PDF":
                return 1;
            case 'DOC':
                return 2;
            case 'DOCX':
                return 3;
            case 'XLS':
                return 4;
            case 'XLSX':
                return 5;
            default:
                return null;
        }
      }

      onSelectAttachment(field: any,event:Event){
        const attachmentType = this.field.parent!.parent!.fieldGroup![3].get!('attachmenttype');
        if (attachmentType.formControl?.value)
        {
          this.fileInput.nativeElement.click();
        } else {
          this.showTooltipMessage({ severity: 'warn', detail: 'You must choose ' + attachmentType.props?.label + ' value before choose a file', life: environment.warningElapsedTime });
        }
      }

      showTooltipMessage(messageStructure: any) {
        this.messageService.add(messageStructure);
      }
    
      async onChange(field: any,event:Event){
          const selectionId = ++this.selectionId;
          const input = event.target as HTMLInputElement;
          const parent = field.formControl.parent;
          if (input.files && input.files.length > 0) {
            const file = input.files[0];
            if (parent) {
              const result=this.getFileNameAndExtension(file.name);
              const fileNameControl = parent.get('attachmentname');
              const fileTypeControl = parent.get('filetype');
              field.formControl.setValue(null);
              fileNameControl?.setValue(null);
              fileTypeControl?.setValue(null);
              let hasError = false;
              let detailedErrorMsg = '';
              if (!this.isValidFileExtension(result.extension)) {
                hasError = true;
                detailedErrorMsg = 'Selected file extension ' + result.extension + ' is not allowed';
              } else if (file.size > environment.maxIndividualByteSize) {
                hasError = true;
                detailedErrorMsg = 'Selected file size exceed maximum size allowed: ' + Util.formatFileSize(environment.maxIndividualByteSize, 0);
              } else {
                try {
                  const bytes = await file.arrayBuffer();
                  if (selectionId !== this.selectionId) return;
                  field.formControl.setValue([new File([bytes], file.name, { type: file.type, lastModified: file.lastModified })]);
                  fileNameControl?.setValue(result.fileName);
                  fileTypeControl?.setValue(this.getFieldTypeValue(result.extension));
                  input.value = '';
                } catch {
                  if (selectionId !== this.selectionId) return;
                  hasError = true;
                  detailedErrorMsg = 'The selected file could not be read. Please select it again.';
                }
              }
              if (hasError) {
                this.showTooltipMessage({ severity: 'error', detail: detailedErrorMsg, life: environment.errorElapsedTime});
                field.formControl.setValue(null); // to remove any previous attached file allowinf required message
                fileNameControl?.setValue(null);
                fileTypeControl?.setValue(null);
                input.value = '';
              }
            }       
          } else {
                if (parent) {
                  const fileNameControl = parent.get('attachmentname');
                  const fileTypeControl = parent.get('filetype');
                  field.formControl.setValue(null); // to remove any previous attached file allowinf required message
                  fileNameControl?.setValue(null);
                  fileTypeControl?.setValue(null);
                  input.value = '';
                }       
          }
      }

      updateAcceptTypes() {
        const options = this.field.parent!.get!('filetype').props!.options;
    
        if (options instanceof Observable) {
          options.subscribe(data => {
            this.acceptTypes = this.calculateAcceptTypes(data);
          });
        } else if (Array.isArray(options)) {
          this.acceptTypes = this.calculateAcceptTypes(options);
        } else {
          this.acceptTypes = '';
        }
      }
    
      calculateAcceptTypes(options: any[]): string {
        return options.map(opt => '.' + opt.name.toLowerCase()).join(',');
      }

      isValidFileExtension(extension: string): boolean {
        let isValid:boolean = false;

        const validExtensions = this.acceptTypes.split(',').map(ext => ext.trim());
        isValid = validExtensions.includes('.' + extension.toLocaleLowerCase());
        
        return isValid;
      }

  isCreateAndSendProductSection() {
    return this.injector.get(Art57Section).isCreateAndSendProductSection(); 
  }
      
}
